/**
 * ReportsScreen — hub for all reporting sub-screens.
 *
 * `/reports`             → a categorised index of report cards.
 * `/reports/:reportId`   → one report, with a back link to the index.
 *
 * A flat tab strip did not scale past the handful of reports here and will not
 * survive the reports the plan adds. The index groups them (Sales · Money ·
 * Stock · Compliance) and each report is its own URL, so deep-links and browser
 * Back work per report rather than per hub.
 *
 * The old `/reports?tab=X` deep-links redirect to `/reports/X`.
 */

import { useParams, useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import type { ComponentType } from 'react';
import {
  TrendingUp, Boxes, ClipboardList, FileText, Scale, Truck, FileCode,
  Wallet, Coins, HandCoins, BookOpen, Tags, PackageX, CalendarClock, LineChart, ShieldAlert, ScrollText, PieChart, Receipt, Undo2, NotebookText, RefreshCcw, Archive, ChevronLeft, ArrowRight, CalendarCheck,
} from 'lucide-react';
import { cn } from '@/utils/cn';
import { usePermissions } from '@/rbac/usePermissions';
import type { Permission } from '@/rbac/permissions';
import { usePlanGate } from '@/plans/usePlanGate';
import { FEATURE_GST_REPORTS, FEATURE_DATA_EXPORT, FEATURE_ADVANCED_REPORTS } from '@/plans/features';
import type { PlanFeature } from '@/plans/features';
import {
  REPORTS_VIEW, REPORTS_DAY_CLOSE, REPORTS_GST, REPORTS_EXPORT,
} from '@/rbac/permissions';
import { SalesReportScreen } from './SalesReportScreen';
import { SalesTrendScreen } from './SalesTrendScreen';
import { DiscountVoidScreen } from './DiscountVoidScreen';
import { SalesByDimensionScreen } from './SalesByDimensionScreen';
import { ProfitLossScreen } from './ProfitLossScreen';
import { ReceivablesScreen } from './ReceivablesScreen';
import { PayablesScreen } from './PayablesScreen';
import { CashBookScreen } from './CashBookScreen';
import { ItemProfitScreen } from './ItemProfitScreen';
import { FilingTrackerScreen } from './FilingTrackerScreen';
import { DeadStockScreen } from './DeadStockScreen';
import { ExpiryScreen } from './ExpiryScreen';
import { StockLedgerScreen } from './StockLedgerScreen';
import { InventoryTurnoverScreen } from './InventoryTurnoverScreen';
import { InventoryValuationScreen } from './InventoryValuationScreen';
import { PurchaseRegisterScreen } from './PurchaseRegisterScreen';
import { PurchaseReturnsScreen } from './PurchaseReturnsScreen';
import { SalesRegisterScreen } from './SalesRegisterScreen';
import { GSTReportScreen } from './GSTReportScreen';
import { StockReportScreen } from './StockReportScreen';
import { DayCloseScreen } from './DayCloseScreen';
import { TallyExportScreen } from './TallyExportScreen';
import { Gstr2bReconScreen } from './Gstr2bReconScreen';
import { EwayBillScreen } from './EwayBillScreen';

// ─── Report catalogue ─────────────────────────────────────────────────────────

type Category = 'Sales' | 'Money' | 'Stock' | 'Compliance';

interface ReportDef {
  id: string;
  label: string;
  blurb: string;
  category: Category;
  icon: ComponentType<{ size?: number | string; className?: string }>;
  Screen: ComponentType;
  /** Whether the current role + plan may see this report. */
  visible: (ctx: GateContext) => boolean;
}

interface GateContext {
  can: (perm: Permission) => boolean;
  canAccess: (feature: PlanFeature) => boolean;
}

const REPORTS: ReportDef[] = [
  {
    id: 'sales', label: 'Sales', category: 'Sales', icon: TrendingUp,
    blurb: 'Revenue, payment split, top products and peak hours over any range.',
    Screen: SalesReportScreen,
    visible: ({ can }) => can(REPORTS_VIEW),
  },
  {
    id: 'sales-trend', label: 'Sales Trend', category: 'Sales', icon: LineChart,
    blurb: 'Net sales over time (day/week/month) with previous-period and last-year overlays.',
    Screen: SalesTrendScreen,
    visible: ({ can }) => can(REPORTS_VIEW),
  },
  {
    id: 'sales-dimension', label: 'Sales by Dimension', category: 'Sales', icon: PieChart,
    blurb: 'Slice net sales by category, customer, hour of day or weekday.',
    Screen: SalesByDimensionScreen,
    visible: ({ can }) => can(REPORTS_VIEW),
  },
  {
    id: 'discount-void', label: 'Discount & Void Audit', category: 'Sales', icon: ShieldAlert,
    blurb: 'Margin given away as discounts and sales cancelled as voids, attributed to each biller.',
    Screen: DiscountVoidScreen,
    visible: ({ can }) => can(REPORTS_VIEW),
  },
  {
    id: 'stock', label: 'Stock', category: 'Stock', icon: Boxes,
    blurb: 'Current inventory valuation with low-stock and out-of-stock alerts.',
    Screen: StockReportScreen,
    visible: ({ can }) => can(REPORTS_VIEW),
  },
  {
    id: 'dead-stock', label: 'Dead Stock', category: 'Stock', icon: PackageX,
    blurb: 'Capital locked in slow-moving and non-selling inventory, over a sales window.',
    Screen: DeadStockScreen,
    visible: ({ can }) => can(REPORTS_VIEW),
  },
  {
    id: 'expiry', label: 'Expiry', category: 'Stock', icon: CalendarClock,
    blurb: 'Batches expired or nearing expiry, with the stock value at risk of write-off.',
    Screen: ExpiryScreen,
    visible: ({ can }) => can(REPORTS_VIEW),
  },
  {
    id: 'stock-ledger', label: 'Stock Ledger', category: 'Stock', icon: ScrollText,
    blurb: 'Every movement of one product with a running balance — shrinkage investigation and audit.',
    Screen: StockLedgerScreen,
    visible: ({ can }) => can(REPORTS_VIEW),
  },
  {
    id: 'turnover', label: 'Inventory Turnover', category: 'Stock', icon: RefreshCcw,
    blurb: 'How many times stock turned over, overall and by category, with days of inventory on hand.',
    Screen: InventoryTurnoverScreen,
    visible: ({ can }) => can(REPORTS_VIEW),
  },
  {
    id: 'valuation', label: 'Inventory Valuation', category: 'Stock', icon: Archive,
    blurb: 'Stock value at cost and retail as of any date — closing stock for the books.',
    Screen: InventoryValuationScreen,
    visible: ({ can }) => can(REPORTS_VIEW),
  },
  {
    id: 'pnl', label: 'Profit & Loss', category: 'Money', icon: Wallet,
    blurb: 'Revenue − cost of goods − expenses = net profit, with margins and period comparison.',
    Screen: ProfitLossScreen,
    // Held by Owner/Manager/Accountant — the finance-trusted roles; excludes
    // Cashier/Viewer/InventoryManager. Profit is more sensitive than the Sales
    // report but the roles carrying REPORTS_VIEW are exactly the right set.
    visible: ({ can }) => can(REPORTS_VIEW),
  },
  {
    id: 'item-profit', label: 'Item Profitability', category: 'Money', icon: Tags,
    blurb: 'Margin per product and category, and the items being sold below cost. Sorted by margin.',
    Screen: ItemProfitScreen,
    visible: ({ can }) => can(REPORTS_VIEW),
  },
  {
    id: 'receivables', label: 'Receivables (Khata)', category: 'Money', icon: Coins,
    blurb: 'Outstanding customer dues aged 0–30 / 31–60 / 61–90 / 90+, flagged against credit limits.',
    Screen: ReceivablesScreen,
    visible: ({ can }) => can(REPORTS_VIEW),
  },
  {
    id: 'payables', label: 'Payables', category: 'Money', icon: HandCoins,
    blurb: 'Supplier outstanding aged 0–30 / 31–60 / 61–90 / 90+, with overdue and credit-limit flags.',
    Screen: PayablesScreen,
    visible: ({ can }) => can(REPORTS_VIEW),
  },
  {
    id: 'cash-book', label: 'Cash Book', category: 'Money', icon: BookOpen,
    blurb: 'Money in vs money out, day by day — receipts, supplier payments, expenses and refunds.',
    Screen: CashBookScreen,
    visible: ({ can }) => can(REPORTS_VIEW),
  },
  {
    id: 'day-close', label: 'Day Close', category: 'Money', icon: ClipboardList,
    blurb: 'Cash reconciliation and Z-report for the current session.',
    Screen: DayCloseScreen,
    visible: ({ can }) => can(REPORTS_DAY_CLOSE),
  },
  {
    id: 'filing-tracker', label: 'Filing Tracker', category: 'Compliance', icon: CalendarCheck,
    blurb: 'Which GSTR-1 and GSTR-3B returns are filed, pending or overdue against their due dates.',
    Screen: FilingTrackerScreen,
    // Reads the same gst_filings records the GST Returns screen writes, so it is
    // gated identically — without filing, every period would read as overdue.
    visible: ({ can, canAccess }) => can(REPORTS_GST) && canAccess(FEATURE_GST_REPORTS),
  },
  {
    id: 'gst', label: 'GST Returns', category: 'Compliance', icon: FileText,
    blurb: 'Monthly GSTR-1 / GSTR-3B summary with portal-ready JSON.',
    Screen: GSTReportScreen,
    visible: ({ can, canAccess }) => can(REPORTS_GST) && canAccess(FEATURE_GST_REPORTS),
  },
  {
    id: 'sales-register', label: 'Sales Register', category: 'Compliance', icon: NotebookText,
    blurb: 'Chronological register of every sale invoice and credit note with its GST split.',
    Screen: SalesRegisterScreen,
    visible: ({ can }) => can(REPORTS_VIEW),
  },
  {
    id: 'purchase-register', label: 'Purchase Register', category: 'Compliance', icon: Receipt,
    blurb: 'Every supplier bill with its GST split and per-supplier totals; flags purchases with no claimable ITC.',
    Screen: PurchaseRegisterScreen,
    visible: ({ can }) => can(REPORTS_VIEW),
  },
  {
    id: 'purchase-returns', label: 'Purchase Returns', category: 'Compliance', icon: Undo2,
    blurb: 'Goods returned to suppliers, rolled up by supplier and by reason.',
    Screen: PurchaseReturnsScreen,
    visible: ({ can }) => can(REPORTS_VIEW),
  },
  {
    id: 'gstr2b', label: 'GSTR-2B Recon', category: 'Compliance', icon: Scale,
    blurb: 'Reconcile purchases against the portal 2B to protect input credit.',
    Screen: Gstr2bReconScreen,
    visible: ({ can, canAccess }) => can(REPORTS_GST) && canAccess(FEATURE_ADVANCED_REPORTS),
  },
  {
    id: 'eway', label: 'E-Way Bill', category: 'Compliance', icon: Truck,
    blurb: 'Generate e-way bill JSON for consignments over the threshold.',
    Screen: EwayBillScreen,
    visible: ({ can, canAccess }) => can(REPORTS_GST) && canAccess(FEATURE_DATA_EXPORT),
  },
  {
    id: 'tally', label: 'Tally Export', category: 'Compliance', icon: FileCode,
    blurb: 'Export vouchers as Tally-compatible XML for your accountant.',
    Screen: TallyExportScreen,
    visible: ({ can, canAccess }) => can(REPORTS_EXPORT) && canAccess(FEATURE_DATA_EXPORT),
  },
];

const CATEGORY_ORDER: Category[] = ['Sales', 'Money', 'Stock', 'Compliance'];

// ─── Component ───────────────────────────────────────────────────────────────

export function ReportsScreen() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { canAccess } = usePlanGate();
  const ctx: GateContext = { can, canAccess };

  const visibleReports = REPORTS.filter((r) => r.visible(ctx));

  // Legacy `/reports?tab=X` → `/reports/X`.
  const [params] = useSearchParams();
  const legacyTab = params.get('tab');
  if (!reportId && legacyTab && visibleReports.some((r) => r.id === legacyTab)) {
    return <Navigate to={`/reports/${legacyTab}`} replace />;
  }

  // A specific report.
  if (reportId) {
    const report = visibleReports.find((r) => r.id === reportId);
    // Unknown id or one the user may not see → back to the index.
    if (!report) return <Navigate to="/reports" replace />;

    const { Screen } = report;
    return (
      <div className="flex h-full flex-col">
        <div className="flex shrink-0 items-center gap-2 border-b border-stroke-hairline/10 px-4 py-3">
          <button
            type="button"
            onClick={() => navigate('/reports')}
            className="flex items-center gap-1 rounded-md px-2 py-1 text-body text-fg-secondary hover:bg-bg-subtle hover:text-fg-primary transition-colors"
          >
            <ChevronLeft size={16} /> Reports
          </button>
          <span className="text-fg-tertiary">/</span>
          <span className="text-body font-medium text-fg-primary">{report.label}</span>
        </div>
        <div className="min-h-0 flex-1 overflow-auto p-4">
          <Screen />
        </div>
      </div>
    );
  }

  // The index.
  return (
    <div className="flex h-full flex-col overflow-auto p-6">
      <h1 className="text-heading font-semibold text-fg-primary">Reports</h1>
      <p className="mt-1 text-body-lg text-fg-secondary">
        Business insight and statutory filings, computed from your local data.
      </p>

      {CATEGORY_ORDER.map((category) => {
        const inCategory = visibleReports.filter((r) => r.category === category);
        if (inCategory.length === 0) return null;
        return (
          <section key={category} className="mt-6">
            <h2 className="text-label uppercase tracking-wide text-fg-tertiary">{category}</h2>
            <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {inCategory.map((report) => (
                <ReportCard key={report.id} report={report} onOpen={() => navigate(`/reports/${report.id}`)} />
              ))}
            </div>
          </section>
        );
      })}

      {visibleReports.length === 0 && (
        <div className="mt-16 text-center text-body text-fg-tertiary">
          No reports are available for your role and plan.
        </div>
      )}
    </div>
  );
}

// ─── Card ────────────────────────────────────────────────────────────────────

function ReportCard({ report, onOpen }: { report: ReportDef; onOpen: () => void }) {
  const { icon: Icon, label, blurb } = report;
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        'group flex flex-col items-start gap-2 rounded-lg border border-stroke-hairline/10 bg-bg-surface p-4 text-left',
        'transition-colors hover:border-accent/30 hover:bg-accent/5',
      )}
    >
      <div className="flex w-full items-center justify-between">
        <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent/10 text-accent-ink">
          <Icon size={18} />
        </span>
        <ArrowRight size={16} className="text-fg-tertiary opacity-0 transition-opacity group-hover:opacity-100" />
      </div>
      <span className="text-body font-medium text-fg-primary">{label}</span>
      {/* `body`, not `label`: this is a sentence explaining the report, and
          12px caps are for labels. The brand's POS floor is 14px for anything
          carrying content. */}
      <span className="text-body text-fg-secondary">{blurb}</span>
    </button>
  );
}
